// ============================================
// pages/transportation/VehiclesMaster.tsx — Vehicle master (real API)
// Compliance tracking: insurance/fitness/permit expiry
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Loader2, Plus, Edit2, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { vehiclesAPI, importAPI } from '../../api/services';
import { MasterImportBar } from '../../components/MasterImportBar';
import { UserRole } from '../../types/markfed';

const STATUS_CLS: Record<string, string> = {
  active:       'bg-green-50 text-green-700 border-green-300',
  maintenance:  'bg-yellow-50 text-yellow-700 border-yellow-300',
  inactive:     'bg-gray-100 text-gray-600 border-gray-300',
  blacklisted:  'bg-red-50 text-red-700 border-red-300',
};

export const VehiclesMaster: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canDelete = hasRole(UserRole.AO_CAO, UserRole.SUPER_ADMIN);

  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (expiringOnly) params.expiring_soon = true;
      const res = await vehiclesAPI.list(params);
      setVehicles(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load vehicles' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, expiringOnly]);

  const isExpired = (date?: string) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const isExpiringSoon = (date?: string) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    const diff = (d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  };

  const handleDelete = async (id: number, vehicleNo: string) => {
    if (!window.confirm(`Delete vehicle ${vehicleNo}?`)) return;
    try {
      await vehiclesAPI.remove(id);
      setVehicles((prev) => prev.filter((v) => v.id !== id));
      setMessage({ type: 'success', text: 'Deleted' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to delete' });
    }
  };

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading && vehicles.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Truck className="w-7 h-7 text-blue-600" />
            Vehicle Master
          </h1>
          <p className="text-sm text-gray-500 mt-1">Compliance tracking — insurance, fitness, permit expiry</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <MasterImportBar
              label="Import Vehicles"
              templatePath="/import/vehicles/template"
              uploader={(f) => importAPI.vehicles(f)}
              onImported={async () => {
                const res = await vehiclesAPI.list();
                setVehicles(Array.isArray(res.data) ? res.data : []);
              }}
              onMessage={setMessage}
            />
            <button onClick={() => navigate('/transportation/vehicles/add')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm">
              <Plus className="w-4 h-4" />Add Vehicle
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[160px]">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="inactive">Inactive</option>
            <option value="blacklisted">Blacklisted</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)}
            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500" />
          Show only vehicles with compliance expiring within 30 days
        </label>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Vehicle No</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Capacity</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Owner / Contractor</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Driver</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Insurance</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Fitness</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Permit</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vehicles.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No vehicles registered.</td></tr>
              ) : vehicles.map((v, i) => {
                const insCls = isExpired(v.insurance_expiry) ? 'text-red-600 font-semibold' : isExpiringSoon(v.insurance_expiry) ? 'text-orange-600 font-semibold' : 'text-gray-600';
                const fitCls = isExpired(v.fitness_expiry) ? 'text-red-600 font-semibold' : isExpiringSoon(v.fitness_expiry) ? 'text-orange-600 font-semibold' : 'text-gray-600';
                const permCls = isExpired(v.permit_expiry) ? 'text-red-600 font-semibold' : isExpiringSoon(v.permit_expiry) ? 'text-orange-600 font-semibold' : 'text-gray-600';
                return (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-900">{v.vehicle_no}</td>
                    <td className="px-3 py-2 text-gray-700 uppercase text-[10px]">{v.vehicle_type}</td>
                    <td className="px-3 py-2 text-right">{v.capacity_mts ? `${v.capacity_mts} MT` : '--'}</td>
                    <td className="px-3 py-2 text-gray-700">{v.owner_name || v.contractor?.firm_name || '--'}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {v.driver_name || '--'}
                      {v.driver_phone && <div className="text-[10px] text-gray-400">{v.driver_phone}</div>}
                    </td>
                    <td className={`px-3 py-2 ${insCls}`}>
                      {formatDate(v.insurance_expiry)}
                      {(isExpired(v.insurance_expiry) || isExpiringSoon(v.insurance_expiry)) && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                    </td>
                    <td className={`px-3 py-2 ${fitCls}`}>{formatDate(v.fitness_expiry)}</td>
                    <td className={`px-3 py-2 ${permCls}`}>{formatDate(v.permit_expiry)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_CLS[v.status] || 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canEdit && (
                          <button onClick={() => navigate(`/transportation/vehicles/edit/${v.id}`)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-3 h-3" /></button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDelete(v.id, v.vehicle_no)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VehiclesMaster;
